import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ForgeBanner } from '@tylertech/forge-react';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import { FeatureFlags } from '@socrata/platform/feature-flags';
import { I18n } from 'common/i18n';
import { testActionEndpoint } from 'common/process_discovery/test-action-endpoint';
import { selectors as storySelectors } from 'store/selectors/StorySelectors';
import { EnvironmentFactory } from 'StorytellerEnvironment';
import { type BlockWithBlockId } from 'store/selectors/StorySelectors/Blocks';

const SESSION_CACHE_PREFIX = 'actions-endpoint-validation:';

const hasActionConfiguredVif = (blocks: BlockWithBlockId[]): boolean =>
  blocks.some(({ components }) =>
    components.some((component) => {
      const config = get(component, 'value.vif.configuration');
      return Boolean(config?.showProcessDiscoveryAction || config?.showExistingActionColumn);
    }),
  );

function isCachedSuccess(url: string): boolean {
  try {
    return sessionStorage.getItem(`${SESSION_CACHE_PREFIX}${url}`) === 'validation-success';
  } catch {
    return false;
  }
}

function cacheSuccess(url: string): void {
  try {
    sessionStorage.setItem(`${SESSION_CACHE_PREFIX}${url}`, 'validation-success');
  } catch {
    // sessionStorage may be unavailable; the next page load will retry
  }
}

export function ActionsConfigBanner() {
  const Environment = EnvironmentFactory();
  const blocks = useSelector(storySelectors.getStoryBlocks(Environment.STORY_UID!), isEqual);
  const [dismissed, setDismissed] = useState(false);

  const actionsFeatureEnabled = FeatureFlags.valueOrDefault('enable_actions_in_tables_for_reports', false);
  const endpointUrl: string = String(get(window, 'socrata.tableActionsConfig.endpoint_url') ?? '').trim();
  const hasActionConfig = useMemo(() => hasActionConfiguredVif(blocks), [blocks]);

  const [endpointInvalid, setEndpointInvalid] = useState(false);

  useEffect(() => {
    setEndpointInvalid(false);

    if (!actionsFeatureEnabled || !hasActionConfig || !endpointUrl || isCachedSuccess(endpointUrl)) {
      return;
    }

    let cancelled = false;
    testActionEndpoint(endpointUrl).then((result) => {
      if (cancelled) {
        return;
      }
      if (result === 'validation-success') {
        cacheSuccess(endpointUrl);
      } else {
        setEndpointInvalid(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [actionsFeatureEnabled, hasActionConfig, endpointUrl]);

  if (!actionsFeatureEnabled || !hasActionConfig || dismissed) {
    return null;
  }

  const shouldShow = !endpointUrl || endpointInvalid;
  if (!shouldShow) {
    return null;
  }

  return (
    <ForgeBanner
      id="actions-config-missing-banner"
      theme="warning"
      data-testid="actions-config-missing-banner"
      on-forge-banner-dismissed={() => setDismissed(true)}
    >
      <div>{I18n.t('common.actions_config_missing_banner')}</div>
    </ForgeBanner>
  );
}
